import { useMemo, useState } from 'react';

import { Bug, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { SettingsRoutes } from '@/router/routes';

import { settingsNavigationConfig } from '../constants';
import { SettingType } from '../types/setting-type';
import { SettingsNavigationGroup } from '../types/settings';

interface SettingsNavigationProps {
  availableSettings: SettingType[];
}

export const SettingsNavigation = ({ availableSettings }: SettingsNavigationProps) => {
  const { t } = useTranslation('authenticated.settings');

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    settingsNavigationConfig.reduce((acc, group) => ({ ...acc, [group.group]: true }), {})
  );

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const filteredNavigationConfig = useMemo(() => {
    const availableTypesSet = new Set(availableSettings);

    const config: SettingsNavigationGroup[] = settingsNavigationConfig
      .map(group => ({
        ...group,
        items: group.items.filter(item => availableTypesSet.has(item.type)),
      }))
      .filter(group => group.items.length > 0);

    // Dev-only debugging group — bypasses backend permission filtering
    if (import.meta.env.DEV) {
      config.push({
        group: 'debugging',
        icon: Bug,
        items: [
          {
            label: 'debugNotifications',
            path: SettingsRoutes.DebugNotifications,
            type: '' as SettingType,
          },
          {
            label: 'framerMockups',
            path: SettingsRoutes.FramerMockups,
            type: '' as SettingType,
          },
        ],
      });
    }

    return config;
  }, [availableSettings]);

  return (
    <aside className="flex h-full w-[220px] flex-col gap-1 overflow-y-auto pb-4 pl-4 pt-4">
      {filteredNavigationConfig.map(({ group, icon: Icon, items }) => {
        const isExpanded = expandedGroups[group];

        return (
          <div key={group} className="flex flex-col gap-1">
            <button
              onClick={() => toggleGroup(group)}
              className="flex h-8 items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-sidebar-accent"
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1 text-left">{t(`navigation.groups.${group}`)}</span>
              <ChevronRight
                className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-90')}
              />
            </button>

            {isExpanded && (
              <div className="relative flex flex-col gap-1 py-0.5 pl-6 pr-3">
                <div className="absolute bottom-0 left-4 top-0 w-px bg-sidebar-border" />
                {items.map(item => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        'flex h-7 items-center rounded-md px-2 text-sm text-foreground hover:bg-sidebar-accent',
                        isActive && 'bg-sidebar-accent font-medium'
                      )
                    }
                  >
                    <span className="truncate">{t(`navigation.items.${item.label}`)}</span>
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </aside>
  );
};
